import React from 'react';
import { Link } from 'react-router-dom';
import { Home, Building, UserCheck, DollarSign, Wrench, FileText, ArrowRight, Loader2 } from 'lucide-react';
import { useCMSContent } from '../hooks/useCMSContent';

export const ServicesPage = () => {
  const iconMap = {
    Home: Home,
    Building: Building,
    UserCheck: UserCheck,
    DollarSign: DollarSign,
    Wrench: Wrench,
    FileText: FileText
  };

  // Fetch CMS content
  const { data: services, loading: servicesLoading } = useCMSContent('services');

  // Show loading state
  if (servicesLoading && !services) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 className="animate-spin" size={48} color="var(--brand-primary)" />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'white' }} data-testid="services-page">
      {/* Page Hero */}
      <section style={{
        backgroundColor: 'var(--bg-section-light)',
        padding: '100px 0 80px',
        borderBottom: '1px solid var(--border-light)'
      }}>
        <div className="container">
          <h1 className="heading-1" style={{ marginBottom: '24px', textAlign: 'center' }}>
            Property Management Services
          </h1>
          <p className="body-large" style={{
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto',
            color: 'var(--text-secondary)'
          }}>
            Comprehensive, full-service property management designed to maximize rental value and minimize owner involvement.
          </p>
        </div>
      </section>

      {/* Main Services - No Blue Boxes */}
      <section className="section-spacing">
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '24px'
          }} data-testid="services-list">
            {(services || []).map((service) => {
              const Icon = iconMap[service.icon] || Home;
              return (
                <div
                  key={service.id || service.documentId}
                  style={{
                    backgroundColor: 'white',
                    padding: '32px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-light)',
                    transition: 'all 0.3s ease'
                  }}
                  className="card-hover"
                  data-testid={`service-item-${service.id || service.documentId}`}
                >
                  <Icon size={36} color="var(--brand-primary)" strokeWidth={1.5} style={{ marginBottom: '20px' }} />
                  <h3 style={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '1.25rem',
                    color: 'var(--text-primary)',
                    marginBottom: '16px'
                  }}>
                    {service.title}
                  </h3>
                  <p style={{
                    color: 'var(--text-secondary)',
                    fontSize: '0.9375rem',
                    lineHeight: 1.7
                  }}>
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Details Split Layout - Your Property Photos */}
      <section className="section-spacing" style={{ backgroundColor: 'var(--bg-section-light)' }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '60px',
            alignItems: 'center'
          }}>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_3edf4311-b53d-4a6a-800b-8fbb025f103f/artifacts/qum9uq25_LRGM%20Prop%205.webp"
                alt="Residential Properties"
                style={{
                  width: '100%',
                  height: '500px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}
              />
            </div>
            <div>
              <h2 className="heading-2" style={{ marginBottom: '24px' }}>
                Residential Property Management
              </h2>
              <p className="body-medium" style={{ marginBottom: '20px', color: 'var(--text-secondary)' }}>
                From single-family homes to large apartment complexes, we provide comprehensive oversight that protects your investment and ensures consistent returns.
              </p>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '15px'
              }}>
                {[
                  'Tenant screening and placement',
                  'Rent collection and accounting',
                  'Property inspections and maintenance',
                  'Lease preparation and enforcement',
                  'Detailed financial reporting'
                ].map((item, i) => (
                  <li key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    color: 'var(--text-secondary)',
                    fontSize: '1rem'
                  }}>
                    <div style={{
                      width: '6px',
                      height: '6px',
                      backgroundColor: 'var(--brand-primary)',
                      borderRadius: '50%',
                      flexShrink: 0
                    }} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Commercial Property Management - Kentucky Photo */}
      <section className="section-spacing">
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '60px',
            alignItems: 'center'
          }}>
            <div style={{ order: 2 }}>
              <img
                src="https://images.unsplash.com/photo-1613013906206-8d2ee5f933a0"
                alt="Commercial Properties"
                style={{
                  width: '100%',
                  height: '500px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}
              />
            </div>
            <div style={{ order: 1 }}>
              <h2 className="heading-2" style={{ marginBottom: '24px' }}>
                Commercial Property Management
              </h2>
              <p className="body-medium" style={{ marginBottom: '20px', color: 'var(--text-secondary)' }}>
                Professional management for office buildings, retail spaces, and commercial properties. We maintain value while ensuring tenant satisfaction.
              </p>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '15px'
              }}>
                {[
                  'Commercial lease negotiation',
                  'Property maintenance coordination',
                  'Tenant relations management',
                  'Building operations oversight',
                  'Compliance and safety standards'
                ].map((item, i) => (
                  <li key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    color: 'var(--text-secondary)',
                    fontSize: '1rem'
                  }}>
                    <div style={{
                      width: '6px',
                      height: '6px',
                      backgroundColor: 'var(--brand-primary)',
                      borderRadius: '50%',
                      flexShrink: 0
                    }} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing" style={{
        background: 'linear-gradient(135deg, #1a3a52 0%, #2563eb 100%)',
        textAlign: 'center'
      }}>
        <div className="container">
          <h2 style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: 'clamp(2rem, 2.5rem + 0.5vw, 2.75rem)',
            color: 'white',
            marginBottom: '16px'
          }}>
            Protect Your Investment
          </h2>
          <p style={{
            fontSize: '1.125rem',
            color: 'rgba(255, 255, 255, 0.9)',
            marginBottom: '32px',
            maxWidth: '700px',
            margin: '0 auto 32px'
          }}>
            Let us handle the details while you focus on growing your portfolio.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn-primary" style={{ backgroundColor: 'white', color: 'var(--brand-primary)' }}>
              Request Management Information
              <ArrowRight size={20} style={{ marginLeft: '8px' }} />
            </Link>
            <Link to="/owners" className="btn-secondary" style={{ backgroundColor: 'transparent', borderColor: 'white', color: 'white', border: '2px solid white' }}>
              Learn More for Owners
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
